import { FOLDER_NAME_MAX_LENGTH } from "@frontend-shared/constants";
import { requiredString } from "@frontend-shared/schema/field-builders";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { StyledInput } from "@/components/StyledInput";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { FORM_VALIDATION_OPTIONS } from "@/lib/validations/form-defaults";

interface ICreateChatFolderDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onCreate: (name: string) => Promise<void>;
}

export const CreateChatFolderDialog: React.FC<ICreateChatFolderDialogProps> = ({
  isOpen,
  onOpenChange,
  onCreate,
}) => {
  const { t } = useTranslation("sidebar");

  const formSchema = useMemo(
    () =>
      z.object({
        name: requiredString({
          requiredMessage: t("chat_folder_list.create_folder.required"),
          maxLength: FOLDER_NAME_MAX_LENGTH,
          maxLengthMessage: t("chat_folder_list.create_folder.max_length", {
            max: FOLDER_NAME_MAX_LENGTH,
          }),
        }),
      }),
    [t],
  );

  const form = useForm<z.infer<typeof formSchema>>({
    ...FORM_VALIDATION_OPTIONS,
    resolver: zodResolver(formSchema),
    defaultValues: { name: "" },
  });

  useEffect(() => {
    if (isOpen) {
      form.reset({ name: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // NOTE: Prevent Radix Dialog auto-focus from triggering onTouched validation on the empty field.
  const handleOpenAutoFocus = useCallback((event: Event): void => {
    event.preventDefault();
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (
    values: z.infer<typeof formSchema>,
  ): Promise<void> => {
    setIsSubmitting(true);
    try {
      await onCreate(values.name.trim());
      onOpenChange(false);
    } catch {
      // NOTE: Dialog stays open so user can retry
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent onOpenAutoFocus={handleOpenAutoFocus}>
        <DialogHeader>
          <DialogTitle>{t("chat_folder_list.create_folder.title")}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field, fieldState: { error } }) => (
                <FormItem>
                  <FormControl>
                    <StyledInput
                      {...field}
                      isError={!!error}
                      placeholder={t(
                        "chat_folder_list.create_folder.placeholder",
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                className="grow"
                onClick={() => onOpenChange(false)}
              >
                {t("chat_folder_list.create_folder.cancel")}
              </Button>
              <Button type="submit" className="grow" disabled={isSubmitting}>
                {t("chat_folder_list.create_folder.create")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
